import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Spinner } from '../../components/common/Spinner';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';
import { COUNTRIES } from '../../utils/constants';
import { Plus, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';

export const AdminCountryConfig: React.FC = () => {
  const { user } = useAuth();
  const isAuditor = user?.role === 'AUDITOR';
  const queryClient = useQueryClient();
  const [filterCountry, setFilterCountry] = useState('');
  const [newCountryCode, setNewCountryCode] = useState('');
  const [newConfigKey, setNewConfigKey] = useState('');
  const [newConfigValue, setNewConfigValue] = useState('');
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'country-config', filterCountry],
    queryFn: () => adminService.getCountryConfigs(filterCountry || undefined),
  });

  const [actionId, setActionId] = useState<string | null>(null);

  const upsertMutation = useMutation({
    mutationFn: ({ countryCode, configKey, configValue }: { countryCode: string; configKey: string; configValue: string }) =>
      adminService.upsertCountryConfig(countryCode, configKey, configValue),
    onSuccess: () => {
      setActionId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'country-config'] });
      toast.success('Country config updated');
    },
    onError: () => { setActionId(null); toast.error('Failed to update country config'); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteCountryConfig(id),
    onSuccess: () => {
      setActionId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'country-config'] });
      toast.success('Country config deleted');
    },
    onError: () => { setActionId(null); toast.error('Failed to delete country config'); },
  });

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  const configs = data?.data || [];

  const handleSave = (config: any) => {
    const value = editValues[config.id] ?? config.configValue;
    setActionId(`save-${config.id}`);
    upsertMutation.mutate({ countryCode: config.countryCode, configKey: config.configKey, configValue: value });
  };

  const handleAdd = () => {
    if (!newCountryCode || !newConfigKey.trim() || !newConfigValue.trim()) return;
    setActionId('__new__');
    upsertMutation.mutate(
      { countryCode: newCountryCode, configKey: newConfigKey.trim(), configValue: newConfigValue.trim() },
      { onSuccess: () => { setNewConfigKey(''); setNewConfigValue(''); } }
    );
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this country config?')) {
      setActionId(`del-${id}`);
      deleteMutation.mutate(id);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Country Configuration</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Per-country overrides for platform settings</p>
      </div>

      <div className="mb-6 w-64">
        <Select
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          options={[
            { value: '', label: 'All Countries' },
            ...COUNTRIES.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` })),
          ]}
        />
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Country</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Config Key</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Value</th>
                {!isAuditor && <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {configs.map((config: any) => (
                <tr key={config.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">{config.country?.name || config.countryCode} ({config.countryCode})</td>
                  <td className="px-6 py-4 font-mono text-sm text-gray-600 dark:text-gray-400">{config.configKey}</td>
                  <td className="px-6 py-4 w-48">
                    <Input
                      value={editValues[config.id] ?? config.configValue}
                      onChange={(e) => setEditValues({ ...editValues, [config.id]: e.target.value })}
                      disabled={isAuditor}
                    />
                  </td>
                  {!isAuditor && (
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSave(config)} isLoading={actionId === `save-${config.id}` && upsertMutation.isPending}>
                          <Save className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(config.id)} isLoading={actionId === `del-${config.id}` && deleteMutation.isPending}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {configs.length === 0 && (
                <tr><td colSpan={isAuditor ? 3 : 4} className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">No country configs found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {!isAuditor && (
        <Card className="mt-8">
          <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4">Add Country Config</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <Select
              label="Country"
              value={newCountryCode}
              onChange={(e) => setNewCountryCode(e.target.value)}
              options={[
                { value: '', label: 'Select country' },
                ...COUNTRIES.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` })),
              ]}
            />
            <Input label="Config Key" value={newConfigKey} onChange={(e) => setNewConfigKey(e.target.value)} placeholder="e.g. min_withdrawal" />
            <Input label="Value" value={newConfigValue} onChange={(e) => setNewConfigValue(e.target.value)} placeholder="e.g. 10" />
            <Button onClick={handleAdd} isLoading={actionId === '__new__' && upsertMutation.isPending}>
              <Plus className="h-4 w-4 mr-2" /> Add
            </Button>
          </div>
        </Card>
      )}
    </AdminLayout>
  );
};
